import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery } from "@apollo/client";
import { useNavigate } from "react-router-dom";
import { Margin, Resolution, usePDF } from "react-to-pdf";

import {
  ASK_VYRILL_QUERY,
  GET_CONVERSATIONS,
  CREATE_CONVERSATION_MUTATION,
} from "../../Queries/ask-vyrill";
import { RESET_ASK_VYRILL_STATE, updateAskVyrillState } from "./Actions";
import { usePrevious } from "../../Shared/CustomHooks";

import LoadingDots from "../Home/components/HomePageLoading/LoadingDots";
import ConversationHistory from "./Components/ConversationHistory";
import SuggestedPrompts from "./Components/SuggestedPrompts";
import ChatBoxHeader from "./Components/ChatBoxHeader";
import AskBarSearch from "./Components/AskBarSearch";
import QuestionRow from "./Components/QuestionRow";
import AnswerRow from "./Components/AnswerRow";
import "./styles/AskVyrill.scss";
import {
  AskVyrillError,
  AskVyrillTimeOutError,
} from "./Components/AskVyrillError";
import moment from "moment";

const createConversation = (brandId, createConversationMutation) => {
  return createConversationMutation({
    variables: {
      brandId,
    },
  });
};

const AskVyrillChat = () => {
  const [isHistoryOpen, setIsHistoryOpen] = useState(true);

  const messages = useSelector((state) => state.askVyrill.messages);

  const askVyrillDuration = useSelector(
    (state) => state.askVyrill.askVyrillDuration
  );
  const custom_start_date = useSelector(
    (state) => state.askVyrill.custom_start_date
  );
  const custom_end_date = useSelector(
    (state) => state.askVyrill.custom_end_date
  );
  const custom_start_date_string = custom_start_date
    ? moment(custom_start_date).toISOString()
    : "";
  const custom_end_date_string = custom_end_date
    ? moment(custom_end_date).toISOString()
    : "";
  const selectedPrompt = useSelector((state) => state.askVyrill.selectedPrompt);

  const currentBrandId = useSelector(
    (state) => state.navbarStatsReducer.currentBrand._id
  );

  const messagesLoading = useSelector(
    (state) => state.askVyrill.messagesLoading
  );
  const askVyrillError = useSelector((state) => state.askVyrill.askVyrillError);
  const loadFromHistory = useSelector(
    (state) => state.askVyrill.loadFromHistory
  );
  const askVyrillTimoutError = useSelector(
    (state) => state.askVyrill.askVyrillTimoutError
  );

  const dispatch = useDispatch();
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();
  const previousBrand = usePrevious(currentBrandId);

  const [
    createConversationMutation, // { data: createConversationData, loading: createConversationLoading, error },
  ] = useMutation(CREATE_CONVERSATION_MUTATION);

  const {
    // loading: askVyrillLoading,
    // error: askVyrillError,
    // data: askVyrillData,
    refetch: refetchAskVyrill,
  } = useQuery(ASK_VYRILL_QUERY, {
    skip: true || !currentBrandId,
    onError: (error) => {
      // Handle the error here
      dispatch(
        updateAskVyrillState({
          askVyrillTimoutError: true,
          messagesLoading: false,
        })
      );
    },
  });

  const {
    // loading: conversationLoading,
    // error: conversationError,
    // data: conversationData,
    refetch: refetchConversation,
  } = useQuery(GET_CONVERSATIONS, {
    variables: { brandId: currentBrandId },
    skip: true,
  });

  useEffect(() => {
    if (currentBrandId && selectedPrompt) {
      dispatch(
        updateAskVyrillState({
          // conversationId: result?.data?.createConversation?._id,
          askVyrillError: false,
          askVyrillTimoutError: false,
        })
      );
      createConversation(currentBrandId, createConversationMutation)
        .then((result) => {
          dispatch(
            updateAskVyrillState({
              conversationId: result?.data?.createConversation?._id,
              messagesLoading: true,
            })
          );

          // Call ask-vyrill after creating conversation
          refetchAskVyrill({
            brandId: currentBrandId,
            conversationId: result?.data?.createConversation?._id,
            duration: askVyrillDuration?.value,
            ...(askVyrillDuration?.value === "custom" &&
            custom_start_date_string &&
            custom_end_date_string
              ? {
                  start_date: custom_start_date_string,
                  end_date: custom_end_date_string,
                }
              : {}),
            content: selectedPrompt,
            role: "user",
          }).then((result) => {
            if (!result?.data?.askVyrill) {
              dispatch(
                updateAskVyrillState({
                  askVyrillError: true,
                  messagesLoading: false,
                })
              );
            } else {
              // Fetching all conversation after creating new
              refetchConversation().then((result) => {
                dispatch(
                  updateAskVyrillState({
                    allConversations: result?.data?.getConversations,
                  })
                );
              });

              const copyMessages = [...messages];
              const message = copyMessages[copyMessages.length - 1];
              message._id = result?.data?.askVyrill?.question_id || ""; //Assigning id to first message
              message.conversation_id =
                result?.data?.askVyrill?.conversation_id || ""; //Assigning id to first message
              dispatch(
                updateAskVyrillState({
                  messages: [...copyMessages, result?.data?.askVyrill],
                  messagesLoading: false,
                  isFirstPrompt: false,
                })
              );
            }
          });
        })
        .catch((error) => {
          console.error("Error in createConversation request:", error);
        });
    } else if (loadFromHistory) {
      // console.log('loadFromHistory')
    } else {
      navigate("/home");
    }
  }, []);

  const handleNavigation = () => {
    navigate("/home");
    // Set default states
    dispatch({ type: RESET_ASK_VYRILL_STATE });
    dispatch(
      updateAskVyrillState({
        askVyrillError: false,
        messagesLoading: false,
        askVyrillTimoutError: false,
      })
    );
  };

  // TODO:for developments
  // useEffect(() => {
  //   if(currentBrandId){

  //     refetchConversation().then((result) => {
  //       dispatch(
  //         updateAskVyrillState({
  //           allConversations: result?.data?.getConversations,
  //         })
  //         );
  //     });
  //   }
  //   }, [currentBrandId]);

  useEffect(() => {
    if (currentBrandId && previousBrand && currentBrandId !== previousBrand) {
      handleNavigation();
    }
  }, [currentBrandId]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // const handlePrint = useReactToPrint({ content: () => printRef.current });

  const { toPDF, targetRef } = usePDF({
    filename: "vyrill.pdf",
    page: { margin: Margin.MEDIUM },
    resolution: Resolution.HIGH,

    canvas: {
      // default is 'image/jpeg' for better size performance
      // mimeType: 'image/jpeg',
      qualityRatio: 1,
    },
  });

  return (
    <div className="AskVyrill">
      <div className="AskVyrillContainer">
        <div className="d-flex justify-content-between">
          <div>
            <SuggestedPrompts isHistoryOpen={isHistoryOpen} />
            <div className="d-flex flex-column">
              <ChatBoxHeader
                isHistoryOpen={isHistoryOpen}
                handlePrint={toPDF}
              />
              <div className="d-flex ">
                <div className="d-flex justify-content-between">
                  <div
                    className={`card-gpt ${
                      isHistoryOpen ? "no-expanded-chat" : "expanded-chat"
                    }`}
                  >
                    <div
                      className="scrollbar-section"
                      id="right-scroll"
                      // style={askVyrillError ? { minHeight: "104px" } : {}}
                    >
                      <div ref={targetRef}>
                        {messages?.map((k, index) => {
                          return (
                            <div key={index}>
                              {k?.role === "user" && <QuestionRow k={k} />}
                              {k?.role === "assistant" && (
                                <AnswerRow k={k} index={index} />
                              )}
                            </div>
                          );
                        })}
                        {askVyrillError && <AskVyrillError />}
                        {askVyrillTimoutError && <AskVyrillTimeOutError />}
                      </div>
                      {messagesLoading && (
                        <div className="d-flex justify-content-center">
                          <LoadingDots />
                        </div>
                      )}
                      <div ref={messagesEndRef} />
                    </div>

                    <div className="chatgpt-search">
                      <AskBarSearch
                        isHistoryOpen={isHistoryOpen}
                        renderFromChat
                      />
                      <div className="chat-prompt-band">
                        <span>
                          This feature is under{" "}
                          <span style={{ color: "#e62580" }}>Beta</span> testing
                          and may provide inaccurate answers. It is still
                          undergoing final testing before its official release.{" "}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ConversationHistory
            setIsHistoryOpen={setIsHistoryOpen}
            isHistoryOpen={isHistoryOpen}
          />{" "}
        </div>
      </div>
      <div style={{ position: "relative" }}>
        {" "}
        <img
          src={require("@src/assets/images/vyrill/cross.png").default}
          alt=""
          role="button"
          width="38px"
          height="38px"
          className="cross-icon"
          onClick={() => {
            handleNavigation();
          }}
        />
      </div>
    </div>
  );
};
export default AskVyrillChat;
